/* eslint-disable react/no-unstable-nested-components */
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useGetMedicalProvidersQuery,
  useMergeMedicalProvidersMutation,
} from '@/services/api/treatmentService';

import { MergePageHeader } from './MergePageHeader';
import { MergeStepPreview } from './MergeStepPreview';
import { MergeStepSelection } from './MergeStepSelection';

export const MergeProvidersPage = () => {
  const navigate = useNavigate();
  const { matter_id } = useParams<{ matter_id: string }>();
  const [searchParams] = useSearchParams();
  const [destinationId, setDestinationId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'select' | 'preview'>(
    'select',
  );

  const providerIds =
    searchParams
      .get('ids')
      ?.split(',')
      .filter((id: string) => id.trim()) || [];

  const {
    data: allProviders,
    isLoading: isLoadingProviders,
    error: providersError,
  } = useGetMedicalProvidersQuery(matter_id!, { skip: !matter_id });

  const [mergeProviders, { isLoading }] = useMergeMedicalProvidersMutation();

  // Filter to only the providers we want to merge
  const providers =
    allProviders?.filter((p: any) => providerIds.includes(p.id)) || [];

  useEffect(() => {
    // Redirect immediately if there's no matter_id
    if (!matter_id) {
      navigate('/matters');
      return;
    }

    // Only check for sufficient providers after the data has loaded
    if (!isLoadingProviders && allProviders) {
      if (providerIds.length < 2 || providers.length < 2) {
        navigate(`/matters/${matter_id}/treatment`);
      }
    }
  }, [
    matter_id,
    providerIds.length,
    providers.length,
    allProviders,
    isLoadingProviders,
    navigate,
  ]);

  // Redirect if no matter_id
  if (!matter_id) {
    return null;
  }

  // Show loading state while providers are being fetched
  if (isLoadingProviders) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Loading Provider Data...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch the provider information.
          </p>
        </div>
      </div>
    );
  }

  // Show error state if providers failed to load
  if (providersError) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="space-y-4">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Error Loading Providers
            </h1>
            <p className="text-gray-600 mb-4">
              Unable to load provider information for matter: {matter_id}
            </p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
          <Button onClick={() => navigate(`/matters/${matter_id}/treatment`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Treatment List
          </Button>
        </div>
      </div>
    );
  }

  // Show invalid selection state
  if (providers.length < 2) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid Provider Selection
          </h1>
          <p className="text-gray-600 mb-4">
            At least 2 providers are required to perform a merge.
          </p>
          <Button onClick={() => navigate(`/matters/${matter_id}/treatment`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Treatment List
          </Button>
        </div>
      </div>
    );
  }

  const handleMerge = async () => {
    if (!destinationId) {
      toast.error('Please select a destination provider');
      return;
    }

    const sourceIds = providers
      .filter((p: any) => p.id !== destinationId)
      .map((p: any) => p.id);

    try {
      await mergeProviders({
        matterId: matter_id!,
        mergeProviderRequest: {
          dst_provider_id: destinationId,
          src_provider_ids: sourceIds,
        },
      }).unwrap();

      toast.success(
        `Successfully merged ${sourceIds.length} provider${sourceIds.length === 1 ? '' : 's'}`,
      );
      navigate(`/matters/${matter_id}/treatment`);
    } catch (error) {
      toast.error('Failed to merge providers');
    }
  };

  const handleCancel = () => {
    navigate(`/matters/${matter_id}/treatment`);
  };

  const handleNext = () => {
    if (!destinationId) {
      toast.error('Please select a destination provider');
      return;
    }
    setCurrentStep('preview');
  };

  const handleBack = () => {
    setCurrentStep('select');
  };

  const destinationProvider = providers.find(
    (p: any) => p.id === destinationId,
  );
  const sourceProviders = providers.filter((p: any) => p.id !== destinationId);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <MergePageHeader
        currentStep={currentStep}
        destinationId={destinationId}
        isLoading={isLoading}
        onCancel={handleCancel}
        onBack={handleBack}
        onNext={handleNext}
        onMerge={handleMerge}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentStep === 'select' ? (
          <MergeStepSelection
            providers={providers}
            destinationId={destinationId}
            onDestinationChange={setDestinationId}
          />
        ) : (
          destinationProvider && (
            <MergeStepPreview
              destinationProvider={destinationProvider}
              sourceProviders={sourceProviders}
            />
          )
        )}
      </div>
    </div>
  );
};
